import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import * as feather from 'feather-icons';
import { ApiService } from 'src/app/api.service';
import { ShopTheming, ThemingService } from 'src/app/theming.service';
import { environment } from 'src/environments/environment';
import { LambdaService } from 'src/app/lambda.service';
import { getFileExtension } from 'src/app/helpers/util';
declare let $: any;

type LogoHelperType = 'desktop' | 'footer' | 'mobile';

interface LogoHelperCard {
  titleKey: string;
  badgeKeys: string[];
  referenceKeys: string[];
  tipKeys: string[];
  formatsKey: string;
  performanceKey?: string;
  contrastKey?: string;
}

// Background configuration interfaces
interface GradientStop {
  color: string;
  position: number; // 0-100
}

interface GradientConfig {
  angle: number; // 0-360 degrees
  stops: GradientStop[];
}

interface BackgroundConfig {
  mode: 'solid' | 'gradient';
  solidColor?: string;
  gradient?: GradientConfig;
}

@Component({
  selector: 'app-theming-settings',
  templateUrl: './theming-settings.component.html',
  styleUrls: ['./theming-settings.component.scss'],
})
export class ThemingSettingsComponent implements OnInit {

  private originalShopMainColor!: string;
  private originalShopSecondaryColor!: string;
  private originalShopHeaderColor!: string;
  private originalShopMainColorDark!: string;
  private fontFamily2!: string;
  public shopTheme: ShopTheming[] = [];
  constructor(
    public apiService: ApiService,
    public themingService: ThemingService,
    private router: Router,
    private lambdaService: LambdaService
  ) {
    this.shopTheme = this.themingService.shopTheming;
    this.shopMainColor = this.shopTheme[0].mainColor.toLocaleUpperCase();
    this.shopSecondaryColor = this.shopTheme[0].mainColorLight.toLocaleUpperCase();
    this.shopHeaderColor = this.shopTheme[0].grayTitleActive.toLocaleUpperCase();
    this.shopMainColorDark = this.shopTheme[0].mainColorDark.toLocaleUpperCase();
    this.selectedFont = this.shopTheme[0].fontFamily2;
    this.syncHeaderSettingsFromTheme();

    // Initialize background configurations
    this.headerBackground = this.initializeHeaderBackground();
    this.footerBackground = this.initializeFooterBackground();
  }

  loading = false;
  isEditingColors = false;
  isEditingImages = false;
  isEditingBanner = false;

  mainLogo = File;
  secondaryLogo = File;
  mobileLogo = File;

  mainLogoFile: any;
  secondaryLogoFile: any;
  mobileLogoFile: any;

  mainLogoFormatNote = '';
  secondaryLogoFormatNote = '';
  mobileLogoFormatNote = '';

  mainLogoUrl = "";
  secondaryLogoUrl = "";
  mobileLogoUrl = "";

  bannersUrl: Array<any>= [];
  bannersMobileUrl: Array<any>= [];
  uploadBanners: Array<any>= [];
  areNewBanners = false;
  banners = null;

  shopMainColor: string;
  shopSecondaryColor: string;
  shopHeaderColor: string;
  shopMainColorDark: string;

  // Background configurations (new gradient system)
  headerBackground: BackgroundConfig;
  footerBackground: BackgroundConfig;
  private originalHeaderBackground!: BackgroundConfig;
  private originalFooterBackground!: BackgroundConfig;

  fonts: any[] = [];
  selectedFont: string = '';
  originalFont: string = '';
  isEditingFonts: boolean = false;

  // Header Settings
  showSearchBar: boolean = true;
  showAuthButtons: boolean = true;
  showCart: boolean = true;
  languageSelectorPosition: 'navbar' | 'footer' = 'navbar';
  isEditingHeaderSettings: boolean = false;
  private originalShowSearchBar!: boolean;
  private originalShowAuthButtons!: boolean;
  private originalShowCart!: boolean;
  private originalLanguageSelectorPosition!: 'navbar' | 'footer';
  isLogoGuideOpen = false;
  activeLogoGuide: LogoHelperType | null = null;
  readonly logoGuideTypes: LogoHelperType[] = ['desktop', 'mobile', 'footer'];
  public readonly logoHelperCards: Record<LogoHelperType, LogoHelperCard> = {
    desktop: {
      titleKey: 'LOGO_HELPER_DESKTOP_TITLE',
      badgeKeys: [
        'LOGO_HELPER_BADGE_SVG_RECOMMENDED',
        'LOGO_HELPER_BADGE_TRANSPARENT_ALT',
        'LOGO_HELPER_BADGE_DESKTOP',
        'LOGO_HELPER_BADGE_DESKTOP_RATIO',
      ],
      referenceKeys: [
        'LOGO_HELPER_REFERENCE_DESKTOP_RATIO',
        'LOGO_HELPER_REFERENCE_DESKTOP_MAX_RATIO',
        'LOGO_HELPER_REFERENCE_DESKTOP_SIZE',
      ],
      tipKeys: [
        'LOGO_HELPER_DESKTOP_TIP_PRIMARY',
        'LOGO_HELPER_DESKTOP_TIP_NAVBAR',
        'LOGO_HELPER_DESKTOP_TIP_RATIO',
        'LOGO_HELPER_DESKTOP_TIP_SIZE',
        'LOGO_HELPER_DESKTOP_TIP_AVOID',
      ],
      formatsKey: 'LOGO_HELPER_DESKTOP_FORMATS',
      performanceKey: 'LOGO_HELPER_DESKTOP_PERFORMANCE',
      contrastKey: 'LOGO_HELPER_DESKTOP_CONTRAST',
    },
    footer: {
      titleKey: 'LOGO_HELPER_FOOTER_TITLE',
      badgeKeys: [
        'LOGO_HELPER_BADGE_DARK_BACKGROUND',
        'LOGO_HELPER_BADGE_TRANSPARENT_BACKGROUND',
        'LOGO_HELPER_BADGE_SQUARE',
        'LOGO_HELPER_BADGE_COMPACT_HORIZONTAL',
      ],
      referenceKeys: [
        'LOGO_HELPER_REFERENCE_SQUARE',
        'LOGO_HELPER_REFERENCE_FOOTER_SIZE_SQUARE',
        'LOGO_HELPER_REFERENCE_FOOTER_SIZE_HORIZONTAL',
      ],
      tipKeys: [
        'LOGO_HELPER_FOOTER_TIP_PRIMARY',
        'LOGO_HELPER_FOOTER_TIP_USAGE',
        'LOGO_HELPER_FOOTER_TIP_TRANSPARENT',
        'LOGO_HELPER_FOOTER_TIP_AVOID',
        'LOGO_HELPER_FOOTER_TIP_SHAPES',
      ],
      formatsKey: 'LOGO_HELPER_FOOTER_FORMATS',
      contrastKey: 'LOGO_HELPER_FOOTER_CONTRAST',
    },
    mobile: {
      titleKey: 'LOGO_HELPER_MOBILE_TITLE',
      badgeKeys: [
        'LOGO_HELPER_BADGE_MOBILE',
        'LOGO_HELPER_BADGE_MOBILE_RATIO',
        'LOGO_HELPER_BADGE_COMPACT',
        'LOGO_HELPER_BADGE_MOBILE_NAVBAR',
      ],
      referenceKeys: [
        'LOGO_HELPER_REFERENCE_MOBILE_RATIO',
        'LOGO_HELPER_REFERENCE_MOBILE_SIZE',
        'LOGO_HELPER_REFERENCE_COMPACT',
      ],
      tipKeys: [
        'LOGO_HELPER_MOBILE_TIP_PRIMARY',
        'LOGO_HELPER_MOBILE_TIP_COMPACT',
        'LOGO_HELPER_MOBILE_TIP_SIZE',
        'LOGO_HELPER_MOBILE_TIP_READABILITY',
        'LOGO_HELPER_MOBILE_TIP_AVOID',
      ],
      formatsKey: 'LOGO_HELPER_MOBILE_FORMATS',
      contrastKey: 'LOGO_HELPER_MOBILE_CONTRAST',
    },
  };

  ngOnInit(): void {
    // this.getBanners()
    this.mainLogoUrl = this.shopTheme[0].shopLogoLarge;
    this.secondaryLogoUrl = this.shopTheme[0].shopLogoWhite;
    this.mobileLogoUrl = this.shopTheme[0].shopLogoSmall;
    this.getFonts()
    this.fontFamily2
    // console.log('fontfamily',this.fontFamily2)
  }

    async getFonts() {
      try {
        const response: any = await this.lambdaService.getFonts();
        this.fonts = Array.isArray(response.data) ? response.data : [];

        // console.log("Fonts parsed:", this.fonts);

        if (!this.selectedFont && this.fonts.length > 0) {
          this.selectedFont = this.shopTheme[0]?.fontFamily2 || this.fonts[0].fontFamily;
        }
      } catch (error) {
        console.log('Error loading fonts:', error);
      }
    }

  switchEditingFonts() {
    if (!this.isEditingFonts) {
      this.originalFont = this.selectedFont;
    }
    this.isEditingFonts = !this.isEditingFonts;
  }

  cancelEditingFonts() {
    this.selectedFont = this.originalFont;
    this.isEditingFonts = false;
  }

  onFontChange(font: string) {
    this.selectedFont = font;
  }

  getSelectedFontName(): string {
    const selected = this.fonts.find(font => font.fontFamily === this.selectedFont);
    return selected ? selected.fontName : '';
  }

  async saveFont() {
    this.loading = true;
    try {
      await this.lambdaService.updateThemingFont(
        this.shopTheme[0].shopId,
        this.selectedFont
      );
      this.shopTheme[0].fontFamily2 = this.selectedFont;
      this.themingService.shopTheming = this.shopTheme;

      $('#themingSuccess').modal('show');
      this.isEditingFonts = false;
    } catch (error) {
      console.error(error);
      $('#themingError').modal('show');
    } finally {
      this.loading = false;
    }
  }

  // Header Settings methods
  switchEditingHeaderSettings() {
    if (!this.isEditingHeaderSettings) {
      this.originalShowSearchBar = this.showSearchBar;
      this.originalShowAuthButtons = this.showAuthButtons;
      this.originalShowCart = this.showCart;
      this.originalLanguageSelectorPosition = this.languageSelectorPosition;
    }
    this.isEditingHeaderSettings = !this.isEditingHeaderSettings;
  }

  cancelEditingHeaderSettings() {
    this.showSearchBar = this.originalShowSearchBar;
    this.showAuthButtons = this.originalShowAuthButtons;
    this.showCart = this.originalShowCart;
    this.languageSelectorPosition = this.originalLanguageSelectorPosition;
    this.isEditingHeaderSettings = false;
  }

  async saveHeaderSettings() {
    this.loading = true;
    try {
      await this.lambdaService.updateThemingHeaderSettings(
        this.shopTheme[0].shopId,
        this.showSearchBar,
        this.showAuthButtons,
        this.showCart,
        this.languageSelectorPosition
      );
      this.shopTheme[0].showSearchBar = this.showSearchBar;
      this.shopTheme[0].showAuthButtons = this.showAuthButtons;
      this.shopTheme[0].showCart = this.showCart;
      this.shopTheme[0].languageSelectorPosition = this.languageSelectorPosition;
      this.themingService.shopTheming = this.shopTheme;
      $('#themingSuccess').modal('show');
      this.isEditingHeaderSettings = false;
    } catch (error) {
      console.error(error);
      $('#themingError').modal('show');
    } finally {
      this.loading = false;
    }
  }

  private syncHeaderSettingsFromTheme() {
    const currentTheme = this.shopTheme[0];
    this.showSearchBar = currentTheme?.showSearchBar ?? true;
    this.showAuthButtons = currentTheme?.showAuthButtons ?? true;
    this.showCart = currentTheme?.showCart ?? true;
    this.languageSelectorPosition =
      currentTheme?.languageSelectorPosition === 'footer' ? 'footer' : 'navbar';
  }

  ngAfterViewInit() {
    feather.replace();
  }

  // Store original colors before editing
  switchEditingColors() {
     if (!this.isEditingColors) {
      this.originalShopMainColor = this.shopMainColor; //Main Color
      this.originalShopSecondaryColor = this.shopSecondaryColor; // Menu Nav-Bar Color
      this.originalShopHeaderColor = this.shopHeaderColor; // Header Color
      this.originalShopMainColorDark = this.shopMainColorDark; // Highlight Color
      
      // Store original background configurations (deep clone)
      this.originalHeaderBackground = JSON.parse(JSON.stringify(this.headerBackground));
      this.originalFooterBackground = JSON.parse(JSON.stringify(this.footerBackground));
    }
    this.isEditingColors = !this.isEditingColors;
  }

  // Restore original colors if editing is canceled
  cancelEditingColors() {
    this.shopMainColor = this.originalShopMainColor;
    this.shopSecondaryColor = this.originalShopSecondaryColor;
    this.shopHeaderColor = this.originalShopHeaderColor;
    this.shopMainColorDark = this.originalShopMainColorDark;
    
    // Restore original background configurations
    this.headerBackground = JSON.parse(JSON.stringify(this.originalHeaderBackground));
    this.footerBackground = JSON.parse(JSON.stringify(this.originalFooterBackground));
    
    this.isEditingColors = false;
  }

  updateThemingColors(){
    this.loading = true;
    
    // Update shopMainColorDark from headerBackground if in solid mode
    if (this.headerBackground.mode === 'solid' && this.headerBackground.solidColor) {
      this.shopMainColorDark = this.headerBackground.solidColor;
    }
    
    console.log('Sending background configurations to backend:', {
      headerBackground: this.headerBackground,
      footerBackground: this.footerBackground
    });
    
    this.lambdaService
    .updateThemingColors(
      this.shopTheme[0].shopId,
      this.shopMainColor,
      this.shopSecondaryColor,
      this.shopHeaderColor,
      this.shopMainColorDark,
      this.headerBackground,
      this.footerBackground
    )
    .then((data: any) => {
      //this.themingService.getThemingService();
      $('#themingSuccess').modal('show');
      this.isEditingColors = !this.isEditingColors;
      this.loading = false;
    })
    .catch((error) => {
      this.loading = false;
      console.error(error);
    });

  }

  onMainLogoSelected(event: any) {
    this.mainLogo = event.target.files[0];
    const file: File = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.mainLogoFile = e.target.result;
        this.setLogoFormatNote(file, 'main');
      };
      reader.readAsDataURL(file);
    } else {
      this.mainLogoFormatNote = '';
    }
  }

  onSecondaryLogoSelected(event: any) {
    this.secondaryLogo = event.target.files[0];
    const file: File = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.secondaryLogoFile = e.target.result;
        this.setLogoFormatNote(file, 'secondary');
      };
      reader.readAsDataURL(file);
    } else {
      this.secondaryLogoFormatNote = '';
    }
  }

  onMobileLogoSelected(event: any) {
    this.mobileLogo = event.target.files[0];
    const file: File = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.mobileLogoFile = e.target.result;
        this.setLogoFormatNote(file, 'mobile');
      };
      reader.readAsDataURL(file);
    } else {
      this.mobileLogoFormatNote = '';
    }
  }

  getMainLogoPreview(): string {
    return this.mainLogoFile || this.mainLogoUrl;
  }

  getSecondaryLogoPreview(): string {
    return this.secondaryLogoFile || this.secondaryLogoUrl;
  }

  getMobileLogoPreview(): string {
    return this.mobileLogoFile || this.mobileLogoUrl;
  }

  getFooterPreviewBackground(): string {
    return this.buildBackgroundStyle(this.footerBackground);
  }

  getDesktopPreviewBackground(): string {
    return '#ffffff';
  }

  getHeaderPreviewBackground(): string {
    return this.buildBackgroundStyle(this.headerBackground);
  }

  getMenuPreviewColor(): string {
    return this.shopMainColor || this.shopMainColorDark;
  }

  getMobileIconPreviewColor(): string {
    return this.shopMainColorDark || this.shopHeaderColor;
  }

  hasSecondaryLogoPreview(): boolean {
    return !!(this.secondaryLogoFile || this.secondaryLogoUrl);
  }

  private getStoredBackgroundConfig(rawConfig: any, fallbackColor: string): BackgroundConfig | null {
    if (!rawConfig || (rawConfig.mode !== 'solid' && rawConfig.mode !== 'gradient')) {
      return null;
    }

    const defaultSolid = this.isValidHexColor(rawConfig.solidColor)
      ? rawConfig.solidColor.toUpperCase()
      : fallbackColor;

    if (rawConfig.mode === 'solid') {
      return {
        mode: 'solid',
        solidColor: defaultSolid
      };
    }

    const gradient = rawConfig.gradient;
    if (!gradient || !Array.isArray(gradient.stops)) {
      return {
        mode: 'gradient',
        solidColor: defaultSolid,
        gradient: {
          angle: 135,
          stops: [
            { color: defaultSolid, position: 0 },
            { color: this.adjustColorBrightness(defaultSolid, 30), position: 100 }
          ]
        }
      };
    }

    const normalizedStops = gradient.stops
      .filter((stop: any) => stop && this.isValidHexColor(stop.color))
      .map((stop: any) => ({
        color: stop.color.toUpperCase(),
        position: this.normalizePosition(Number(stop.position))
      }));

    if (normalizedStops.length < 2) {
      return {
        mode: 'gradient',
        solidColor: defaultSolid,
        gradient: {
          angle: this.normalizeAngle(Number(gradient.angle) || 135),
          stops: [
            { color: defaultSolid, position: 0 },
            { color: this.adjustColorBrightness(defaultSolid, 30), position: 100 }
          ]
        }
      };
    }

    return {
      mode: 'gradient',
      solidColor: defaultSolid,
      gradient: {
        angle: this.normalizeAngle(Number(gradient.angle) || 135),
        stops: normalizedStops
      }
    };
  }

  // Background configuration initialization
  private initializeHeaderBackground(): BackgroundConfig {
    const fallbackColor = this.shopMainColorDark;
    const stored = this.getStoredBackgroundConfig(this.shopTheme[0]?.headerBackground, fallbackColor);

    if (stored) {
      return stored;
    }

    // Initialize from shopMainColorDark (evolution of existing field)
    return {
      mode: 'solid',
      solidColor: fallbackColor
    };
  }

  private initializeFooterBackground(): BackgroundConfig {
    const fallbackColor = this.shopMainColor || this.shopSecondaryColor || this.shopMainColorDark;
    const stored = this.getStoredBackgroundConfig(this.shopTheme[0]?.footerBackground, fallbackColor);

    if (stored) {
      return stored;
    }

    // Initialize as new field with fallback to current footer preview logic
    return {
      mode: 'solid',
      solidColor: fallbackColor
    };
  }

  // Build CSS string from background configuration
  buildBackgroundStyle(config: BackgroundConfig): string {
    if (!config) {
      return '#CCCCCC'; // Safe fallback
    }

    if (config.mode === 'solid' && config.solidColor) {
      return config.solidColor;
    }

    if (config.mode === 'gradient' && config.gradient) {
      const { angle, stops } = config.gradient;
      
      if (!stops || stops.length < 2) {
        // Fallback to solid if gradient is invalid
        return config.solidColor || '#CCCCCC';
      }

      // Sort stops by position
      const sortedStops = [...stops].sort((a, b) => a.position - b.position);
      
      // Build gradient stops string
      const stopsString = sortedStops
        .map(stop => `${stop.color} ${stop.position}%`)
        .join(', ');

      return `linear-gradient(${angle}deg, ${stopsString})`;
    }

    return config.solidColor || '#CCCCCC';
  }

  // Gradient stop management
  addGradientStop(config: BackgroundConfig): void {
    if (!config.gradient) {
      config.gradient = {
        angle: 90,
        stops: []
      };
    }

    if (config.gradient.stops.length >= 5) {
      return; // Maximum 5 stops
    }

    // Find the largest gap between stops and insert new stop at midpoint
    const stops = [...config.gradient.stops].sort((a, b) => a.position - b.position);
    
    let newPosition = 50;
    if (stops.length === 0) {
      newPosition = 0;
    } else if (stops.length === 1) {
      newPosition = 100;
    } else {
      // Find largest gap
      let maxGap = 0;
      let insertPosition = 50;
      
      for (let i = 0; i < stops.length - 1; i++) {
        const gap = stops[i + 1].position - stops[i].position;
        if (gap > maxGap) {
          maxGap = gap;
          insertPosition = stops[i].position + gap / 2;
        }
      }
      
      newPosition = Math.round(insertPosition);
    }

    // Default color: slightly lighter/darker than existing colors
    const defaultColor = stops.length > 0 ? stops[0].color : '#EA803F';

    config.gradient.stops.push({
      color: defaultColor,
      position: newPosition
    });
  }

  removeGradientStop(config: BackgroundConfig, index: number): void {
    if (!config.gradient || !config.gradient.stops) {
      return;
    }

    if (config.gradient.stops.length <= 2) {
      return; // Minimum 2 stops required
    }

    config.gradient.stops.splice(index, 1);
  }

  // Switch background mode (solid <-> gradient)
  switchBackgroundMode(config: BackgroundConfig, newMode: 'solid' | 'gradient'): void {
    if (config.mode === newMode) {
      return;
    }

    config.mode = newMode;

    if (newMode === 'gradient' && !config.gradient) {
      // Initialize gradient with 2 stops using current solid color
      const baseColor = config.solidColor || '#EA803F';
      config.gradient = {
        angle: 135,
        stops: [
          { color: baseColor, position: 0 },
          { color: this.adjustColorBrightness(baseColor, 30), position: 100 }
        ]
      };
    }
  }

  // Helper to adjust color brightness for default gradient
  private adjustColorBrightness(hex: string, percent: number): string {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Adjust brightness
    const adjust = (value: number) => {
      const adjusted = value + (percent / 100) * 255;
      return Math.max(0, Math.min(255, Math.round(adjusted)));
    };
    
    const newR = adjust(r);
    const newG = adjust(g);
    const newB = adjust(b);
    
    // Convert back to hex
    const toHex = (value: number) => {
      const hex = value.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    
    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`.toUpperCase();
  }

  // Validation helpers
  normalizeAngle(angle: number): number {
    if (isNaN(angle)) return 0;
    let normalized = angle % 360;
    if (normalized < 0) normalized += 360;
    return normalized;
  }

  normalizePosition(position: number): number {
    if (isNaN(position)) return 0;
    return Math.max(0, Math.min(100, position));
  }

  isValidHexColor(color: string): boolean {
    return /^#[0-9A-F]{6}$/i.test(color);
  }

  openLogoGuide(type: LogoHelperType) {
    this.activeLogoGuide = type;
    this.isLogoGuideOpen = true;
  }

  setActiveLogoGuide(type: LogoHelperType) {
    this.activeLogoGuide = type;
  }

  closeLogoGuide() {
    this.isLogoGuideOpen = false;
    this.activeLogoGuide = null;
  }

  getActiveLogoGuideCard(): LogoHelperCard | null {
    return this.activeLogoGuide ? this.logoHelperCards[this.activeLogoGuide] : null;
  }

  @HostListener('document:keydown.escape')
  handleEscapeKey() {
    if (this.isLogoGuideOpen) {
      this.closeLogoGuide();
    }
  }

  private setLogoFormatNote(file: File, target: 'main' | 'secondary' | 'mobile') {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      const noteKey = this.getLogoFormatNote(image.width, image.height);
      if (target === 'main') {
        this.mainLogoFormatNote = noteKey;
      }
      if (target === 'secondary') {
        this.secondaryLogoFormatNote = noteKey;
      }
      if (target === 'mobile') {
        this.mobileLogoFormatNote = noteKey;
      }
      URL.revokeObjectURL(objectUrl);
    };

    image.onerror = () => {
      if (target === 'main') {
        this.mainLogoFormatNote = '';
      }
      if (target === 'secondary') {
        this.secondaryLogoFormatNote = '';
      }
      if (target === 'mobile') {
        this.mobileLogoFormatNote = '';
      }
      URL.revokeObjectURL(objectUrl);
    };

    image.src = objectUrl;
  }

  private getLogoFormatNote(width: number, height: number): string {
    if (!width || !height) {
      return '';
    }

    const ratio = width / height;
    if (ratio >= 1.25) {
      return 'LOGO_FORMAT_HORIZONTAL';
    }

    if (ratio <= 0.85) {
      return 'LOGO_FORMAT_VERTICAL';
    }

    return 'LOGO_FORMAT_SQUARE';
  }

  async saveThemingLogo(){
    if(this.mainLogo instanceof File || this.secondaryLogo instanceof File || this.mobileLogo instanceof File) {
      $('#loadingModal').modal('show');
      if(this.mainLogo instanceof File){
        let ext = getFileExtension(this.mainLogo);
        await this.lambdaService.uploadFile(this.shopTheme[0].shopId+'/'+environment.stage+'/images/logos/mainlogo.'+ext,this.mainLogo,{})
        .then((data: any) => {
          this.mainLogoUrl = data.imageURL;
        })
      }
      if(this.secondaryLogo instanceof File){
        let ext = getFileExtension(this.secondaryLogo);
        await this.lambdaService.uploadFile(this.shopTheme[0].shopId+'/'+environment.stage+'/images/logos/secondarylogo.'+ext,this.secondaryLogo,{})
        .then((data: any) => {
          this.secondaryLogoUrl = data.imageURL;
        })
      }
      if(this.mobileLogo instanceof File){
        let ext = getFileExtension(this.mobileLogo);
        await this.lambdaService.uploadFile(this.shopTheme[0].shopId+'/'+environment.stage+'/images/logos/mobilelogo.'+ext,this.mobileLogo,{})
        .then((data: any) => {
          this.mobileLogoUrl = data.imageURL;
        })
      }
      await this.lambdaService
      .updateThemingLogo(
        this.shopTheme[0].shopId,
        this.mainLogoUrl,
        this.secondaryLogoUrl,
        this.mobileLogoUrl
      )
      .then((resp: any ) =>{
        $('#loadingModal').modal('hide');
        $('#themingSuccess').modal('show');
        this.shopTheme[0].shopLogoLarge = this.mainLogoUrl;
        this.shopTheme[0].shopLogoWhite = this.secondaryLogoUrl;
        this.shopTheme[0].shopLogoSmall = this.mobileLogoUrl;
        this.themingService.shopTheming = this.shopTheme;
        //this.themingService.getThemingService();
        this.resetLogoDrafts();
        this.closeLogoGuide();
        this.isEditingImages = !this.isEditingImages;
      })
    }else{
      $('#themingEmpty').modal('show');
    }
  }

/*   getBanners(){
    this.areNewBanners = false;
    this.bannersUrl = []
    this.bannersMobileUrl = []
    this.apiService
    .getBanners(this.themingService.shopId)
    .then((data: any) => {
      this.banners = data.sort((a:any, b:any) => a.bannerMobile - b.bannerMobile);;

      this.bannersUrl = data.filter((banner:any) => banner.bannerMobile === 0).map((banner:any) =>
      {
        return {
          id: banner.bannerId,
          bannerURL: banner.bannerURL
        }
      });
      this.bannersMobileUrl = data.filter((banner:any) => banner.bannerMobile === 1).map((banner:any) =>
      {
        return {
          id: banner.bannerId,
          bannerURL: banner.bannerURL
        }
      });

      while (this.bannersUrl.length < 3) {
        this.bannersUrl.push({});
      }
      while (this.bannersMobileUrl.length < 3) {
        this.bannersMobileUrl.push({});
      }
    })
  } */

  onBannerSelected(event: Event, index: number, array: any) {
    let bannerElement = event.target as HTMLInputElement;
    if(bannerElement.files && bannerElement.files.length > 0){
      const bannerFile = bannerElement.files[0];
      if (bannerFile) {
        array[index].file = bannerFile;
        const reader = new FileReader();
        reader.onload = e => array[index].file.preview = reader.result;
        reader.readAsDataURL(bannerFile);
      }
    }
  }

  async uploadBannerImages(){
    this.uploadBanners = []
    const uploadPromises: any = [];
    this.bannersUrl.forEach((banner, index) => {
      if(banner.bannerURL && !banner.file){
        this.uploadBanners.push({bannerURL: banner.bannerURL, bannerName: "slider"+index, bannerMobile: 0, bannerId: banner.id});
      }
      if(banner.file){
        if(banner.file instanceof File){
          let ext = getFileExtension(banner.file);
          let name = "slide-"+index+"-web.";
          uploadPromises.push(this.lambdaService.uploadFile(this.shopTheme[0].shopId+'/'+environment.stage+'/images/banners/'+name+ext,banner.file,{})
          .then((data: any) => {
            this.uploadBanners.push({bannerURL: data.imageURL, bannerName: "slider"+index, bannerMobile: 0});
          }));
        }
      }
    });
    this.bannersMobileUrl.forEach((banner, index) => {
      if(banner.bannerURL && !banner.file){
        this.uploadBanners.push({bannerURL: banner.bannerURL, bannerName: "slider"+index, bannerMobile: 1, bannerId: banner.id});
      }
      if(banner.file){
        if(banner.file instanceof File){
          let ext = getFileExtension(banner.file);
          let name = "slide-"+index+"-mobile.";
          uploadPromises.push(this.lambdaService.uploadFile(this.shopTheme[0].shopId+'/'+environment.stage+'/images/banners/'+name+ext,banner.file,{})
          .then((data: any) => {
            this.uploadBanners.push({bannerURL: data.imageURL, bannerName: "slider"+index, bannerMobile: 1});
          }))
        }
      }
    });
    await Promise.all(uploadPromises);
  }

  async saveThemingBanners(){
    $('#loadingModal').modal('show');
    await this.uploadBannerImages();

    this.uploadBanners.forEach((banner:any) => {
      if(!banner.bannerId){
        this.areNewBanners = true;
      }
    })
    if(this.areNewBanners){
      try{
          await this.apiService
          .updateThemingBanners(
            this.shopTheme[0].shopId,
            this.uploadBanners
          )
          .then((resp: any ) =>{
            if(resp.status != 200){
              throw new Error("Error");
            }
            // this.getBanners();
            $('#themingSuccess').modal('show');
            this.switchEditingBanner();
            $('#loadingModal').modal('hide');
          })
        }catch(err){
          $('#themingError').modal('hide');
        }
    }else{
     // this.getBanners();
      this.switchEditingBanner();
      $('#themingEmpty').modal('show');
      $('#loadingModal').modal('hide');
    }
  }

  deleteBannerImage(index: number, array: any){
    array[index] = {};
    this.areNewBanners = true;
  }

  switchEditingBanner() {
    this.isEditingBanner = !this.isEditingBanner;
  }

  switchEditingImages() {
    if (this.isEditingImages) {
      this.closeLogoGuide();
    }
    this.isEditingImages = !this.isEditingImages;
  }

  cancelEditingImages() {
    this.resetLogoDrafts();
    this.closeLogoGuide();
    this.isEditingImages = false;
  }

  private resetLogoDrafts() {
    this.mainLogo = File;
    this.secondaryLogo = File;
    this.mobileLogo = File;
    this.mainLogoFile = null;
    this.secondaryLogoFile = null;
    this.mobileLogoFile = null;
    this.mainLogoFormatNote = '';
    this.secondaryLogoFormatNote = '';
    this.mobileLogoFormatNote = '';
  }

  closeModal(){
    $('#themingSuccess').modal('hide');
    $('#themingError').modal('hide');
    $('#themingEmpty').modal('hide');
  }

}
